// TelcoTrack — weld-layout v2 validation and milestone completion.
// Cassette rules: rack position bounds/overlap, per-cassette content, slot occupancy,
// aggregate weld tray capacity for direct links.

import {
  cabinetCassetteRackForType,
  cabinetCassetteRackSection,
  isRozvadecConnectionKindAllowed,
  splitterRatioOutputCount,
  type RozvadecCabinetCassetteRack,
} from './rozvadec-cabinet.constants';
import {
  getRozvadecCassetteType,
  isRozvadecCassetteTypeId,
  isRozvadecWeldTrayCassette,
  isRozvadecWeldTraySeat,
  rozvadecCassetteOccupiedPositions,
  type RozvadecCassetteTypeDef,
} from './rozvadec-cassette.constants';
import { hasRozvadecOfsmRack } from './rozvadec-ofsm.constants';
import type { RsuOccupancy } from './rozvadec-rsu-occupancy.types';
import {
  defaultRozvadecWeldLayoutOffers,
  deriveRozvadecWeldLayoutCounts,
  disconnectNonOsCassetteBypassDirects,
  findWeldLayoutBuilding,
  findWeldLayoutCable,
  isOsDirectOnlyWeldType,
  resolveRozvadecWeldLayoutCapacity,
  resolveWeldFiberCapacity,
  rozvadecWeldTrayCapacity,
  sanitizeClientWeldLayoutShape,
} from './rozvadec-weld-layout.derive';
import {
  hasWeldCassetteSlot,
  isCompleteWeldIncomingRef,
  isCompleteWeldTargetRef,
  isPersistedWeldLayoutId,
  rozvadecWeldCassetteSlotKey,
  rozvadecWeldIncomingRefKey,
  rozvadecWeldRackPositionKey,
  rozvadecWeldSplitterOutputKey,
  rozvadecWeldTargetKey,
  trimId,
} from './rozvadec-weld-layout.ids';
import { normalizeRozvadecWeldLayout } from './rozvadec-weld-layout.normalize';
import {
  isOccupancyFiberFiberPair,
  layoutWeldConnections,
  parseOccupancyEndpointId,
  validateCassetteOccupancyConnections,
  type OccupancyConnectionIssueCode,
} from './rozvadec-occupancy-connection';
import {
  incomingRefsEqual,
  isRozvadecCassetteBoundDirect,
  isRozvadecCassetteBypassDirect,
  isRozvadecSplitterReservedLink,
  rozvadecCassetteWeldUsedCount,
  weldLinkCassetteId,
  weldLinkIncomingRefs,
  weldLinkPeerIncoming,
  weldLinkSeat,
} from './rozvadec-weld-layout.pairs';
import {
  ROZVADEC_WELD_LAYOUT_VERSION,
  isRozvadecWeldLinkKind,
  isRozvadecWeldTerminationKind,
  type RozvadecWeldCassette,
  type RozvadecWeldIncomingRef,
  type RozvadecWeldLayout,
  type RozvadecWeldLayoutCompletion,
  type RozvadecWeldLayoutContext,
  type RozvadecWeldLayoutIssue,
  type RozvadecWeldLayoutIssueCode,
  type RozvadecWeldLink,
  type RozvadecWeldTermination,
} from './rozvadec-weld-layout.types';

function issue(
  code: RozvadecWeldLayoutIssueCode,
  path: string,
  message: string,
): RozvadecWeldLayoutIssue {
  return { code, path, message };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function rawVersion(raw: unknown): unknown {
  return isRecord(raw) ? raw['version'] : undefined;
}

function uniqueGroup<T>(
  items: readonly T[],
  keyOf: (item: T) => string | null,
): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  items.forEach((item, index) => {
    const key = keyOf(item);
    if (!key) return;
    const list = groups.get(key);
    if (list) list.push(index);
    else groups.set(key, [index]);
  });
  return groups;
}

function pushDuplicates(
  issues: RozvadecWeldLayoutIssue[],
  groups: Map<string, number[]>,
  code: RozvadecWeldLayoutIssueCode,
  pathPrefix: string,
  message: string,
): void {
  for (const indexes of groups.values()) {
    if (indexes.length < 2) continue;
    for (const index of indexes) {
      issues.push(issue(code, `${pathPrefix}[${index}]`, message));
    }
  }
}

export type RozvadecWeldLayoutValidateOptions = {
  hadUnitLabel?: boolean;
  rawVersion?: unknown;
  /**
   * When set (including empty), PUT persist semantics: reject new cassette-bypass
   * directs on non-OS and disconnect stored ones. Omit for display / D5 / parity.
   */
  persistedLinkIds?: readonly string[] | ReadonlySet<string>;
  /** RSU/ODF RAY-OFSM welds — used for counts / completion, not cassette validation. */
  rsuOccupancy?: Pick<RsuOccupancy, 'weldConnections' | 'patchConnections'> | null;
};

function allowedLinkKind(typeId: string | null | undefined, kind: string): boolean {
  if (kind === 'direct' || kind === 'connector' || kind === 'splitter') {
    if (!typeId) return kind === 'direct';
    return isRozvadecConnectionKindAllowed(typeId, kind);
  }
  return false;
}

function validateCassetteItem(
  cassette: RozvadecWeldCassette,
  index: number,
  rack: RozvadecCabinetCassetteRack | null,
): RozvadecWeldLayoutIssue[] {
  const path = `cassettes[${index}]`;
  const issues: RozvadecWeldLayoutIssue[] = [];
  const def = getRozvadecCassetteType(cassette.cassetteType);
  if (!isRozvadecCassetteTypeId(cassette.cassetteType) || !def) {
    issues.push(issue('CASSETTE_TYPE_INVALID', `${path}.cassetteType`, 'Unknown cassette type.'));
    return issues;
  }
  if (!rack) {
    issues.push(
      issue('CASSETTE_TYPE_INVALID', path, 'This cabinet type has no cassette rack.'),
    );
    return issues;
  }
  if (!rack.allowedCassetteTypes.includes(def.id)) {
    issues.push(
      issue('CASSETTE_TYPE_INVALID', `${path}.cassetteType`, 'Cassette type is not allowed in this cabinet.'),
    );
  }
  const section = cabinetCassetteRackSection(rack, cassette.sectionId);
  if (!section) {
    issues.push(issue('CASSETTE_POSITION_INVALID', `${path}.sectionId`, 'Unknown rack section.'));
    return issues;
  }
  if (
    !Number.isInteger(cassette.position) ||
    cassette.position < 0 ||
    cassette.position >= section.positions
  ) {
    issues.push(issue('CASSETTE_POSITION_INVALID', `${path}.position`, 'Rack position is out of range.'));
    return issues;
  }
  if (cassette.position + def.span > section.positions) {
    issues.push(
      issue(
        'CASSETTE_SPAN_OUT_OF_SECTION',
        `${path}.position`,
        `Cassette spans ${def.span} positions and does not fit the section.`,
      ),
    );
  }
  return issues;
}

function validateCassetteOverlaps(
  cassettes: readonly RozvadecWeldCassette[],
): RozvadecWeldLayoutIssue[] {
  const issues: RozvadecWeldLayoutIssue[] = [];
  const coverage = new Map<string, number[]>();
  cassettes.forEach((cassette, index) => {
    for (const position of rozvadecCassetteOccupiedPositions(cassette.cassetteType, cassette.position)) {
      const key = rozvadecWeldRackPositionKey(cassette.sectionId, position);
      const list = coverage.get(key);
      if (list) list.push(index);
      else coverage.set(key, [index]);
    }
  });
  const flagged = new Set<number>();
  for (const indexes of coverage.values()) {
    if (indexes.length < 2) continue;
    for (const index of indexes) flagged.add(index);
  }
  for (const index of [...flagged].sort((a, b) => a - b)) {
    issues.push(
      issue('CASSETTE_POSITION_OVERLAP', `cassettes[${index}]`, 'Cassette overlaps another cassette.'),
    );
  }
  return issues;
}

function validateTerminationPlacement(
  termination: RozvadecWeldTermination,
  path: string,
  rack: RozvadecCabinetCassetteRack | null,
  cassettesById: ReadonlyMap<string, RozvadecWeldCassette>,
): RozvadecWeldLayoutIssue[] {
  const issues: RozvadecWeldLayoutIssue[] = [];
  const cassetteId = trimId(termination.cassetteId);
  const slot = termination.slotInCassette;

  if (!cassetteId) {
    if (slot != null) {
      issues.push(
        issue('TERMINATION_CASSETTE_MISSING', `${path}.slotInCassette`, 'Slot requires a cassette reference.'),
      );
    }
    const looseAllowed =
      !rack || (rack.splittersOutsideCassettes && termination.kind === 'splitter');
    if (!looseAllowed) {
      issues.push(
        issue('TERMINATION_CASSETTE_MISSING', `${path}.cassetteId`, 'Termination must be placed in a cassette.'),
      );
    }
    return issues;
  }

  const cassette = cassettesById.get(cassetteId);
  if (!cassette) {
    issues.push(
      issue('TERMINATION_CASSETTE_MISSING', `${path}.cassetteId`, 'Referenced cassette does not exist.'),
    );
    return issues;
  }
  const def = getRozvadecCassetteType(cassette.cassetteType);
  if (!def) return issues; // cassette already flagged as CASSETTE_TYPE_INVALID

  if (slot == null || !Number.isInteger(slot) || slot < 1) {
    issues.push(
      issue('TERMINATION_CONTENT_NOT_ALLOWED', `${path}.slotInCassette`, 'Slot in cassette must be a 1-based integer.'),
    );
    return issues;
  }

  if (termination.kind === 'connector') {
    if (!def.contentKinds.includes('connector') || def.adapterCount == null) {
      issues.push(
        issue('TERMINATION_CONTENT_NOT_ALLOWED', path, 'This cassette has no connector adapters.'),
      );
      return issues;
    }
    const portLimit = def.connectorPortCount ?? def.adapterCount;
    if (slot > portLimit) {
      issues.push(
        issue(
          'CASSETTE_ADAPTER_CAPACITY_EXCEEDED',
          `${path}.slotInCassette`,
          `Adapter slot exceeds cassette capacity ${portLimit}.`,
        ),
      );
    }
    return issues;
  }

  // splitter in a cassette — only preinstalled SAP variants
  const preinstalled = def.preinstalledSplitters;
  if (!preinstalled || !def.contentKinds.includes('splitter')) {
    issues.push(
      issue('CASSETTE_SPLITTER_NOT_PREINSTALLED', path, 'This cassette has no preinstalled splitters.'),
    );
    return issues;
  }
  if (slot > preinstalled.count) {
    issues.push(
      issue(
        'CASSETTE_SPLITTER_NOT_PREINSTALLED',
        `${path}.slotInCassette`,
        `Cassette has only ${preinstalled.count} preinstalled splitter(s).`,
      ),
    );
  }
  if (termination.splitterRatio && termination.splitterRatio !== preinstalled.ratio) {
    issues.push(
      issue(
        'CASSETTE_SPLITTER_NOT_PREINSTALLED',
        `${path}.splitterRatio`,
        `Preinstalled splitter ratio is ${preinstalled.ratio}.`,
      ),
    );
  }
  return issues;
}

function validateTerminationItem(
  termination: RozvadecWeldTermination,
  index: number,
  context: RozvadecWeldLayoutContext,
  offers: RozvadecWeldLayout['offers'],
  rack: RozvadecCabinetCassetteRack | null,
  cassettesById: ReadonlyMap<string, RozvadecWeldCassette>,
): RozvadecWeldLayoutIssue[] {
  const path = `terminations[${index}]`;
  const issues: RozvadecWeldLayoutIssue[] = [];
  if (!isRozvadecWeldTerminationKind(termination.kind)) {
    issues.push(issue('TERMINATION_KIND_INVALID', `${path}.kind`, 'Termination kind must be connector or splitter.'));
    return issues;
  }
  if (!offers.connectorsEnabled) {
    issues.push(issue('OFFERS_CONNECTORS_DISABLED', path, 'Connectors are not enabled; terminations are forbidden.'));
  }
  if (!allowedLinkKind(context.typeId, termination.kind)) {
    issues.push(issue('TERMINATION_KIND_NOT_ALLOWED', `${path}.kind`, 'Termination kind is not allowed for this cabinet.'));
  }
  issues.push(...validateTerminationPlacement(termination, path, rack, cassettesById));
  if (!isCompleteWeldIncomingRef(termination.incoming)) {
    if (termination.kind !== 'splitter') {
      issues.push(issue('TERMINATION_INCOMING_INVALID', `${path}.incoming`, 'Incoming cable and 1-based fiber are required.'));
    }
  } else {
    const catalog = context.incomingCables;
    if (catalog && catalog.length > 0) {
      const cable = findWeldLayoutCable(catalog, termination.incoming.cableId);
      if (!cable) {
        issues.push(issue('TERMINATION_INCOMING_UNKNOWN', `${path}.incoming`, 'Incoming cable is not in the feeder catalog.'));
      } else {
        const capacity = resolveWeldFiberCapacity(cable);
        if (capacity == null) {
          issues.push(
            issue(
              'TERMINATION_INCOMING_CAPACITY_UNKNOWN',
              `${path}.incoming`,
              'Incoming cable fiber capacity is unknown.',
            ),
          );
        } else if (
          termination.incoming.physicalFiber < 1 ||
          termination.incoming.physicalFiber > capacity
        ) {
          issues.push(
            issue(
              'TERMINATION_INCOMING_OUT_OF_CAPACITY',
              `${path}.incoming.physicalFiber`,
              'Incoming fiber is outside the feeder capacity.',
            ),
          );
        }
      }
    }
  }
  if (termination.kind === 'splitter') {
    if (!termination.splitterRatio) {
      issues.push(issue('TERMINATION_RATIO_REQUIRED', `${path}.splitterRatio`, 'Splitter ratio is required.'));
    } else if (splitterRatioOutputCount(termination.splitterRatio) == null) {
      issues.push(issue('TERMINATION_RATIO_INVALID', `${path}.splitterRatio`, 'Splitter ratio is not an approved 1:N value.'));
    }
  } else if (termination.splitterRatio) {
    issues.push(issue('TERMINATION_RATIO_FORBIDDEN', `${path}.splitterRatio`, 'Connector termination cannot carry a splitter ratio.'));
  }
  return issues;
}

function validateAdapterAggregates(
  layout: RozvadecWeldLayout,
): RozvadecWeldLayoutIssue[] {
  const issues: RozvadecWeldLayoutIssue[] = [];
  const byCassette = new Map<string, { def: RozvadecCassetteTypeDef; connectorCount: number }>();
  const cassettesById = new Map(layout.cassettes.map((cassette) => [cassette.id, cassette]));
  for (const termination of layout.terminations) {
    if (termination.kind !== 'connector') continue;
    const cassetteId = trimId(termination.cassetteId);
    if (!cassetteId) continue;
    const cassette = cassettesById.get(cassetteId);
    const def = cassette ? getRozvadecCassetteType(cassette.cassetteType) : undefined;
    if (!def || def.adapterCount == null) continue;
    const entry = byCassette.get(cassetteId) ?? { def, connectorCount: 0 };
    entry.connectorCount += 1;
    byCassette.set(cassetteId, entry);
  }
  for (const [cassetteId, entry] of byCassette) {
    if (entry.connectorCount <= (entry.def.adapterCount ?? 0)) continue;
    issues.push(
      issue(
        'CASSETTE_ADAPTER_CAPACITY_EXCEEDED',
        'terminations',
        `Cassette ${cassetteId} holds ${entry.connectorCount} connectors; capacity is ${entry.def.adapterCount}.`,
      ),
    );
  }
  return issues;
}

/** Complete cassette pairs + building directs consume KM4/KM6 tray capacity. */
function validateWeldTrayCapacity(
  layout: RozvadecWeldLayout,
  rack: RozvadecCabinetCassetteRack | null,
): RozvadecWeldLayoutIssue[] {
  if (!rack) return [];
  const used = rozvadecCassetteWeldUsedCount(layout);
  if (used === 0) return [];
  const capacity = rozvadecWeldTrayCapacity(layout.cassettes);
  if (used <= capacity) return [];
  return [
    issue(
      'CASSETTE_WELD_CAPACITY_EXCEEDED',
      'links',
      `Direct welds ${used} exceed installed weld tray capacity ${capacity} (KM4/KM6).`,
    ),
  ];
}

function findCassettePairFiberCable(
  context: RozvadecWeldLayoutContext,
  cableId: string,
): { typOk?: string | null; fiberCount?: number | null } | null {
  const incoming = findWeldLayoutCable(context.incomingCables, cableId);
  if (incoming) return incoming;
  const outgoing = findWeldLayoutCable(context.outgoingCables, cableId);
  if (outgoing) return outgoing;
  const building = (context.buildings ?? []).find(
    (item) => (item.cableId ?? '').trim() === cableId.trim(),
  );
  return building ?? null;
}

function validateIncomingFiber(
  incoming: RozvadecWeldIncomingRef,
  path: string,
  context: RozvadecWeldLayoutContext,
  terminatedIncoming: Set<string>,
): RozvadecWeldLayoutIssue[] {
  const issues: RozvadecWeldLayoutIssue[] = [];
  if (terminatedIncoming.has(rozvadecWeldIncomingRefKey(incoming))) {
    issues.push(
      issue('LINK_DIRECT_SOURCE_TERMINATED', path, 'Direct link cannot use a terminated feeder fiber.'),
    );
  }
  const cable = findCassettePairFiberCable(context, incoming.cableId);
  if (!cable) {
    const catalogsEmpty =
      !(context.incomingCables?.length)
      && !(context.outgoingCables?.length)
      && !(context.buildings?.length);
    if (catalogsEmpty) return issues;
    issues.push(issue('LINK_SOURCE_INVALID', path, 'Direct source cable is not in the feeder catalog.'));
    return issues;
  }
  const capacity = resolveWeldFiberCapacity(cable);
  if (capacity == null) {
    issues.push(issue('TERMINATION_INCOMING_CAPACITY_UNKNOWN', path, 'Incoming cable fiber capacity is unknown.'));
  } else if (incoming.physicalFiber < 1 || incoming.physicalFiber > capacity) {
    issues.push(
      issue('TERMINATION_INCOMING_OUT_OF_CAPACITY', `${path}.physicalFiber`, 'Incoming fiber is outside the feeder capacity.'),
    );
  }
  return issues;
}

function validateDirectLink(
  link: RozvadecWeldLink,
  path: string,
  layout: RozvadecWeldLayout,
  context: RozvadecWeldLayoutContext,
  terminatedIncoming: Set<string>,
): RozvadecWeldLayoutIssue[] {
  const issues: RozvadecWeldLayoutIssue[] = [];
  const cassetteId = weldLinkCassetteId(link);
  const peer = weldLinkPeerIncoming(link);
  const hasPeerRaw = link.peerIncoming != null;
  const hasTarget = isCompleteWeldTargetRef(link.target);
  const cassetteBound = cassetteId.length > 0 || peer != null || (hasPeerRaw && !hasTarget);

  if (hasTarget && (cassetteId || peer)) {
    issues.push(
      issue(
        'LINK_DIRECT_SHAPE_INVALID',
        path,
        'A direct link is either a cassette incoming pair or a building target, not both.',
      ),
    );
  }

  if (!isCompleteWeldIncomingRef(link.incoming)) {
    issues.push(issue('LINK_SOURCE_INVALID', `${path}.incoming`, 'Direct link requires a free incoming pair.'));
  } else {
    issues.push(...validateIncomingFiber(link.incoming, `${path}.incoming`, context, terminatedIncoming));
  }

  if (cassetteBound && !hasTarget) {
    if (!cassetteId) {
      issues.push(issue('LINK_CASSETTE_MISSING', `${path}.cassetteId`, 'Cassette pair requires a weld-tray cassette.'));
      return issues;
    }
    const cassette = layout.cassettes.find((item) => item.id === cassetteId) ?? null;
    if (!cassette) {
      issues.push(issue('LINK_CASSETTE_MISSING', `${path}.cassetteId`, 'Referenced cassette does not exist.'));
    } else if (!isRozvadecWeldTrayCassette(cassette.cassetteType)) {
      issues.push(
        issue('LINK_CASSETTE_NOT_WELD_TRAY', `${path}.cassetteId`, 'Direct pairs can only live in a KM4/KM6 weld tray.'),
      );
    } else {
      const seat = weldLinkSeat(link);
      if (seat != null && !isRozvadecWeldTraySeat(cassette.cassetteType, seat)) {
        issues.push(issue('LINK_SEAT_INVALID', `${path}.seat`, 'Weld seat is outside the tray capacity.'));
      }
    }
    if (hasPeerRaw && !peer) {
      issues.push(issue('LINK_PEER_INVALID', `${path}.peerIncoming`, 'Peer incoming fiber is incomplete.'));
    } else if (peer) {
      if (incomingRefsEqual(link.incoming, peer)) {
        issues.push(
          issue('LINK_PEER_SAME_AS_SOURCE', `${path}.peerIncoming`, 'A cassette pair cannot splice a fiber to itself.'),
        );
      }
      issues.push(...validateIncomingFiber(peer, `${path}.peerIncoming`, context, terminatedIncoming));
    }
    return issues;
  }

  return issues;
}

function validateLinkItem(
  link: RozvadecWeldLink,
  index: number,
  layout: RozvadecWeldLayout,
  context: RozvadecWeldLayoutContext,
  terminatedIncoming: Set<string>,
  options?: RozvadecWeldLayoutValidateOptions,
): RozvadecWeldLayoutIssue[] {
  const path = `links[${index}]`;
  const issues: RozvadecWeldLayoutIssue[] = [];
  if (!isRozvadecWeldLinkKind(link.kind)) {
    issues.push(issue('LINK_KIND_INVALID', `${path}.kind`, 'Link kind must be direct, connector, or splitter.'));
    return issues;
  }
  if (
    options?.persistedLinkIds != null &&
    isRozvadecCassetteBypassDirect(link) &&
    !isPersistedWeldLayoutId(link.id)
  ) {
    issues.push(
      issue(
        'OFFERS_DIRECT_DISABLED',
        path,
        'Přímo kabel na kabel lze svařit jen v kazetě nebo na ploše optické spojky.',
      ),
    );
  }
  if ((link.kind === 'connector' || link.kind === 'splitter') && !layout.offers.connectorsEnabled) {
    issues.push(issue('OFFERS_CONNECTORS_DISABLED', path, 'Connector/splitter links require connectorsEnabled.'));
  }
  if (!allowedLinkKind(context.typeId, link.kind)) {
    issues.push(issue('LINK_KIND_NOT_ALLOWED', `${path}.kind`, 'Link kind is not allowed for this cabinet.'));
  }

  if (link.kind === 'direct') {
    issues.push(
      ...validateDirectLink(
        link,
        path,
        layout,
        context,
        terminatedIncoming,
      ),
    );
  } else {
    if (link.peerIncoming != null) {
      issues.push(
        issue(
          'LINK_PEER_INVALID',
          `${path}.peerIncoming`,
          'Adapter / splitter links cannot carry a second bare incoming fiber. Rear splice lives on the termination.',
        ),
      );
    }
    if (link.kind === 'connector' && link.incoming != null) {
      issues.push(
        issue(
          'LINK_SOURCE_INVALID',
          `${path}.incoming`,
          'Connector rear splice is termination.incoming; the front link must not carry another cable fiber.',
        ),
      );
    }
    const terminationId = (link.terminationId ?? '').trim();
    const termination = layout.terminations.find((item) => item.id === terminationId);
    if (!terminationId || !termination) {
      issues.push(issue('LINK_TERMINATION_MISSING', `${path}.terminationId`, 'Link must reference an existing termination.'));
    } else if (termination.kind !== link.kind) {
      issues.push(
        issue(
          'LINK_TERMINATION_KIND_MISMATCH',
          `${path}.terminationId`,
          'Link kind must match the referenced termination.',
        ),
      );
    }
    if (link.kind === 'splitter') {
      const outputs = termination ? splitterRatioOutputCount(termination.splitterRatio) : null;
      if (
        link.splitterOutput == null ||
        !Number.isInteger(link.splitterOutput) ||
        outputs == null ||
        link.splitterOutput < 1 ||
        link.splitterOutput > outputs
      ) {
        issues.push(
          issue('LINK_SPLITTER_OUTPUT_INVALID', `${path}.splitterOutput`, 'Splitter output must be in 1..N for the ratio.'),
        );
      }
    }
  }

  if (link.kind === 'direct' && isRozvadecCassetteBoundDirect(link)) {
    return issues;
  }
  if (link.kind === 'splitter' && isRozvadecSplitterReservedLink(link)) {
    const reserved = link.incoming;
    if (isCompleteWeldIncomingRef(reserved)) {
      issues.push(...validateIncomingFiber(reserved, `${path}.incoming`, context, terminatedIncoming));
    }
    return issues;
  }

  if (!isCompleteWeldTargetRef(link.target)) {
    issues.push(issue('LINK_TARGET_INVALID', `${path}.target`, 'Target building, záfuk cable and 1-based fiber are required.'));
  } else if (context.buildings && context.buildings.length > 0) {
    const building = findWeldLayoutBuilding(context.buildings, link.target.buildingId);
    if (!building) {
      issues.push(issue('LINK_TARGET_BUILDING_UNKNOWN', `${path}.target.buildingId`, 'Target building is not linked to this cabinet.'));
    } else if (!(building.cableId ?? '').trim()) {
      issues.push(issue('LINK_TARGET_NO_CABLE', `${path}.target`, 'Building has no specified optical záfuk.'));
    } else if ((building.cableId ?? '').trim() !== link.target.cableId.trim()) {
      issues.push(issue('LINK_TARGET_CABLE_MISMATCH', `${path}.target.cableId`, 'Target cable must be the building záfuk.'));
    } else {
      const capacity = resolveWeldFiberCapacity(building);
      if (capacity == null) {
        issues.push(issue('LINK_TARGET_CAPACITY_UNKNOWN', `${path}.target`, 'Building záfuk fiber capacity is unknown.'));
      } else if (link.target.physicalFiber < 1 || link.target.physicalFiber > capacity) {
        issues.push(issue('LINK_TARGET_OUT_OF_CAPACITY', `${path}.target.physicalFiber`, 'Target fiber is outside the záfuk capacity.'));
      }
    }
  }
  return issues;
}

function validateCapacity(
  layout: RozvadecWeldLayout,
  context: RozvadecWeldLayoutContext,
): RozvadecWeldLayoutIssue[] {
  const issues: RozvadecWeldLayoutIssue[] = [];
  const counts = deriveRozvadecWeldLayoutCounts(layout);
  const capacity = resolveRozvadecWeldLayoutCapacity({
    ...context,
    cassettes: context.cassettes ?? layout.cassettes,
  });
  if (capacity.primaryLimit != null && counts.weldCountPrimary > capacity.primaryLimit) {
    issues.push(
      issue(
        'CAPACITY_PRIMARY_EXCEEDED',
        'terminations',
        `Primary count ${counts.weldCountPrimary} exceeds capacity ${capacity.primaryLimit}.`,
      ),
    );
  }
  if (capacity.secondaryLimit != null && counts.weldCountSecondary > capacity.secondaryLimit) {
    issues.push(
      issue(
        'CAPACITY_SECONDARY_EXCEEDED',
        'links',
        `Secondary count ${counts.weldCountSecondary} exceeds capacity ${capacity.secondaryLimit}.`,
      ),
    );
  }
  if (capacity.unknownOsLimit && counts.weldCountSecondary > 0) {
    issues.push(issue('CAPACITY_OS_UNKNOWN', 'links', 'OS weld cap is unknown; secondary links cannot be confirmed.'));
  }
  if (capacity.connectorLimit != null && counts.connectorTerminationCount > capacity.connectorLimit) {
    issues.push(
      issue(
        'CAPACITY_CONNECTOR_EXCEEDED',
        'terminations',
        `Connector count ${counts.connectorTerminationCount} exceeds connector field ${capacity.connectorLimit}.`,
      ),
    );
  }
  return issues;
}

export function validateRozvadecWeldLayout(
  layout: RozvadecWeldLayout,
  context: RozvadecWeldLayoutContext = {},
  options?: RozvadecWeldLayoutValidateOptions,
): RozvadecWeldLayoutIssue[] {
  const issues: RozvadecWeldLayoutIssue[] = [];
  if (
    options?.rawVersion !== undefined &&
    options.rawVersion !== ROZVADEC_WELD_LAYOUT_VERSION
  ) {
    issues.push(issue('VERSION_UNSUPPORTED', 'version', 'Only weld-layout contract version 2 is supported.'));
  }
  if (options?.hadUnitLabel) {
    issues.push(issue('LINK_UNIT_LABEL_FORBIDDEN', 'layout', 'unitLabel / unitId are not part of weld-layout v2.'));
  }
  if (!layout.offers.directEnabled && !layout.offers.connectorsEnabled) {
    issues.push(issue('OFFERS_NONE_ENABLED', 'offers', 'At least one of directEnabled or connectorsEnabled is required.'));
  }
  const allowed = defaultRozvadecWeldLayoutOffers(context.typeId);
  if (isOsDirectOnlyWeldType(context.typeId) && layout.offers.connectorsEnabled) {
    issues.push(issue('OFFERS_OS_DIRECT_ONLY', 'offers.connectorsEnabled', 'OS cabinets allow direct welds only.'));
  } else if (layout.offers.connectorsEnabled && !allowed.connectorsEnabled) {
    issues.push(issue('OFFERS_OS_DIRECT_ONLY', 'offers.connectorsEnabled', 'This cabinet type does not allow connectors.'));
  }
  if (!isOsDirectOnlyWeldType(context.typeId)) {
    layoutWeldConnections(layout).forEach((weld, index) => {
      const from = parseOccupancyEndpointId(weld.fromEndpointId);
      const to = parseOccupancyEndpointId(weld.toEndpointId);
      if (from && to && isOccupancyFiberFiberPair(from, to)) {
        issues.push(
          issue(
            'LINK_KIND_NOT_ALLOWED',
            `weldConnections[${index}]`,
            'Přímo kabel na kabel lze svařit jen v kazetě nebo na ploše optické spojky.',
          ),
        );
      }
    });
  }

  const rack = cabinetCassetteRackForType(context.typeId);
  layout.cassettes.forEach((cassette, index) => {
    issues.push(...validateCassetteItem(cassette, index, rack));
  });
  issues.push(...validateCassetteOverlaps(layout.cassettes));

  const cassettesById = new Map(layout.cassettes.map((cassette) => [cassette.id, cassette]));
  layout.terminations.forEach((termination, index) => {
    issues.push(
      ...validateTerminationItem(termination, index, context, layout.offers, rack, cassettesById),
    );
  });
  pushDuplicates(
    issues,
    uniqueGroup(layout.terminations, (item) =>
      hasWeldCassetteSlot(item)
        ? rozvadecWeldCassetteSlotKey(item.cassetteId as string, item.slotInCassette as number)
        : null,
    ),
    'TERMINATION_SLOT_OCCUPIED',
    'terminations',
    'Cassette slot is already occupied.',
  );
  pushDuplicates(
    issues,
    uniqueGroup(layout.terminations, (item) =>
      isCompleteWeldIncomingRef(item.incoming) ? rozvadecWeldIncomingRefKey(item.incoming) : null,
    ),
    'TERMINATION_INCOMING_OCCUPIED',
    'terminations',
    'Incoming feeder fiber is already terminated.',
  );
  issues.push(...validateAdapterAggregates(layout));

  const terminatedIncoming = new Set(
    layout.terminations.flatMap((item) =>
      isCompleteWeldIncomingRef(item.incoming) ? [rozvadecWeldIncomingRefKey(item.incoming)] : [],
    ),
  );
  layout.links.forEach((link, index) => {
    issues.push(...validateLinkItem(link, index, layout, context, terminatedIncoming, options));
  });
  const incomingSourceIndexes = new Map<string, number[]>();
  layout.links.forEach((link, index) => {
    if (link.kind === 'direct' || isRozvadecSplitterReservedLink(link)) {
      for (const ref of weldLinkIncomingRefs(link)) {
        const key = `direct:${rozvadecWeldIncomingRefKey(ref)}`;
        const list = incomingSourceIndexes.get(key);
        if (list) list.push(index);
        else incomingSourceIndexes.set(key, [index]);
      }
      return;
    }
    if (link.kind === 'connector' && (link.terminationId ?? '').trim()) {
      const key = `connector:${(link.terminationId ?? '').trim()}`;
      const list = incomingSourceIndexes.get(key);
      if (list) list.push(index);
      else incomingSourceIndexes.set(key, [index]);
    }
  });
  pushDuplicates(
    issues,
    incomingSourceIndexes,
    'LINK_SOURCE_OCCUPIED',
    'links',
    'Link source is already used.',
  );
  pushDuplicates(
    issues,
    uniqueGroup(layout.links, (link) => {
      const seat = weldLinkSeat(link);
      const cassetteId = weldLinkCassetteId(link);
      if (!isRozvadecCassetteBoundDirect(link) || !cassetteId || seat == null) return null;
      return `${cassetteId}#${seat}`;
    }),
    'LINK_SEAT_OCCUPIED',
    'links',
    'Weld seat is already used.',
  );
  pushDuplicates(
    issues,
    uniqueGroup(layout.links, (link) => {
      if (
        link.kind !== 'splitter' ||
        !(link.terminationId ?? '').trim() ||
        link.splitterOutput == null ||
        !Number.isInteger(link.splitterOutput)
      ) {
        return null;
      }
      return rozvadecWeldSplitterOutputKey(link.terminationId as string, link.splitterOutput);
    }),
    'LINK_SPLITTER_OUTPUT_OCCUPIED',
    'links',
    'Splitter output is already used.',
  );
  pushDuplicates(
    issues,
    uniqueGroup(layout.links, (link) =>
      isCompleteWeldTargetRef(link.target) ? rozvadecWeldTargetKey(link.target) : null,
    ),
    'LINK_TARGET_OCCUPIED',
    'links',
    'Target building fiber is already used.',
  );

  issues.push(...validateWeldTrayCapacity(layout, rack));
  issues.push(...validateCapacity(layout, context));
  return issues;
}

export function validateRozvadecWeldLayoutInput(
  raw: unknown,
  context: RozvadecWeldLayoutContext = {},
  options?: Pick<RozvadecWeldLayoutValidateOptions, 'persistedLinkIds'>,
): { layout: RozvadecWeldLayout; issues: RozvadecWeldLayoutIssue[] } {
  const normalized = normalizeRozvadecWeldLayout(raw);
  const persistGate = options?.persistedLinkIds != null;
  const sanitized = sanitizeClientWeldLayoutShape(normalized.layout, context.typeId);
  const createIssues: RozvadecWeldLayoutIssue[] = [];
  if (persistGate) {
    sanitized.links.forEach((link, index) => {
      if (isRozvadecCassetteBypassDirect(link) && !isPersistedWeldLayoutId(link.id)) {
        createIssues.push(
          issue(
            'OFFERS_DIRECT_DISABLED',
            `links[${index}]`,
            'Přímo kabel na kabel lze svařit jen v kazetě nebo na ploše optické spojky.',
          ),
        );
      }
    });
  }
  const layout = persistGate
    ? disconnectNonOsCassetteBypassDirects(sanitized, context.typeId).layout
    : sanitized;
  const occupancyIssues = persistGate
    ? validateCassetteOccupancyConnections(layout, {
        draft: true,
        allowDirectFiberFiber: isOsDirectOnlyWeldType(context.typeId),
      }).map((item) =>
        issue(
          occupancyIssueToWeldCode(item.code),
          item.path,
          item.message,
        ),
      )
    : [];
  return {
    layout,
    issues: [
      ...createIssues,
      ...validateRozvadecWeldLayout(layout, context, {
        hadUnitLabel: normalized.hadUnitLabel,
        rawVersion: rawVersion(raw),
        persistedLinkIds: options?.persistedLinkIds,
      }),
      ...occupancyIssues,
    ],
  };
}

function occupancyIssueToWeldCode(code: OccupancyConnectionIssueCode): RozvadecWeldLayoutIssueCode {
  switch (code) {
    case 'PATCH_PORT_OCCUPIED':
    case 'WELD_PORT_OCCUPIED':
    case 'WELD_FIBER_OCCUPIED':
      return 'LINK_SOURCE_OCCUPIED';
    case 'DIRECT_CABLE_CABLE_FORBIDDEN':
      return 'LINK_KIND_NOT_ALLOWED';
    case 'CONNECTION_DIRECTION_UNKNOWN':
      return 'LINK_SOURCE_INVALID';
    case 'SPLITTER_OUT_OCCUPIED':
      return 'LINK_SPLITTER_OUTPUT_OCCUPIED';
    case 'CONNECTOR_TYPE_INCOMPATIBLE':
      return 'LINK_KIND_NOT_ALLOWED';
    case 'SPLITTER_OUT_USED_AS_IN':
      return 'LINK_SPLITTER_OUTPUT_INVALID';
    case 'OUTGOING_AS_PATH_START':
    case 'SPLITTER_PATH_INCOMPLETE':
    case 'ORIENTATION_NEEDS_CABLE_ROLE':
    case 'ENDPOINT_UNKNOWN':
      return 'LINK_SOURCE_INVALID';
    case 'SPLITTER_PATH_CYCLIC':
      return 'LINK_DIRECT_SHAPE_INVALID';
    default:
      return 'LINK_SOURCE_INVALID';
  }
}

export function uniqueSortedIssueCodes(
  issues: readonly RozvadecWeldLayoutIssue[],
): RozvadecWeldLayoutIssueCode[] {
  return [...new Set(issues.map((item) => item.code))].sort((a, b) => a.localeCompare(b));
}

export function evaluateRozvadecWeldLayoutCompletion(
  layout: RozvadecWeldLayout,
  context: RozvadecWeldLayoutContext,
  mode: 'primary' | 'secondary' | 'unified' = 'unified',
  options?: RozvadecWeldLayoutValidateOptions,
): RozvadecWeldLayoutCompletion {
  const issues = validateRozvadecWeldLayout(layout, context, options);
  const counts = deriveRozvadecWeldLayoutCounts(layout, { rsuOccupancy: options?.rsuOccupancy });
  const unknownOsCapacity = issues.some((item) => item.code === 'CAPACITY_OS_UNKNOWN');
  if (issues.length > 0) {
    return { complete: false, counts, issues, unknownOsCapacity };
  }
  // RSU/ODF: physical OFSM welds, not cassette pairs / buildings / directEnabled.
  if (hasRozvadecOfsmRack(context.typeId)) {
    return { complete: counts.weldCountTotal > 0, counts, issues, unknownOsCapacity };
  }
  if (mode === 'unified') {
    return { complete: counts.weldCount > 0, counts, issues, unknownOsCapacity };
  }
  if (mode === 'primary') {
    const complete = layout.offers.directEnabled || counts.weldCountPrimary > 0;
    return { complete, counts, issues, unknownOsCapacity };
  }
  return { complete: counts.weldCountSecondary > 0, counts, issues, unknownOsCapacity };
}
